/**
 * WidgetRenderer — mounts the display widgets attached to an assistant turn.
 *
 * The SERVER decided the hierarchy; this component only renders it (partition.ts):
 *   - one primary widget → a FULL card, the rest as COMPACT secondary chips;
 *   - no primary ("all-compact" turn, e.g. "how's my business?") → every widget
 *     as a compact chip, no full card.
 * Suggestion widgets (class 'suggestion') render as a quiet offer BELOW the
 * display widgets — a follow-on after the data, never mixed into the hierarchy.
 * Unknown class/type is dropped; each card is wrapped in an error boundary so a
 * single malformed widget degrades to nothing instead of throwing. Renders null
 * when nothing survives (small talk / no widgets) — no empty-card artifacts.
 */
import React from 'react'
import { CopilotWidget } from '../lib/copilotTypes'
import { partitionWidgets } from './partition'
import { FULL_CARDS } from './cards'
import CompactChip from './CompactChip'
import SuggestionCard from './SuggestionCard'
import WidgetBoundary from './WidgetBoundary'
import styles from './widgets.module.scss'

const FullCard: React.FC<{ widget: CopilotWidget }> = ({ widget }) => {
  const Card = FULL_CARDS[widget.type as string]
  if (!Card) return null
  return <Card widget={widget} />
}

const key = (w: CopilotWidget, i: number): string => `${w.type || 'w'}_${i}`

const WidgetRenderer: React.FC<{ widgets?: CopilotWidget[] }> = ({ widgets }) => {
  const { primary, compact, allCompact, suggestions, hasContent } = partitionWidgets(widgets)
  if (!hasContent) return null

  return (
    <div className={styles.widgets}>
      {primary && (
        <WidgetBoundary>
          <FullCard widget={primary} />
        </WidgetBoundary>
      )}
      {compact.length > 0 && (
        <div className={allCompact ? styles.chipRow : styles.secondaryRow}>
          {compact.map((w, i) => (
            <WidgetBoundary key={key(w, i)}>
              <CompactChip widget={w} />
            </WidgetBoundary>
          ))}
        </div>
      )}
      {suggestions.length > 0 && (
        <div className={styles.suggestions}>
          {suggestions.map((w, i) => (
            <WidgetBoundary key={`s_${key(w, i)}`}>
              <SuggestionCard widget={w} />
            </WidgetBoundary>
          ))}
        </div>
      )}
    </div>
  )
}

export default WidgetRenderer
